import type { TrailingAnalyticsView, TrendWindowView } from '@contracts';
import { lifeAreaLabel, lifeAreaTokenPrefix } from './LifeAreaTagSelect';
import { TodayCard } from './primitives';

/**
 * Row F — three analytics cards (R8.10, design/screens/today-desktop-v2.md
 * SS12). Mood/14d (bar chart) and Energy/14d (line chart, + sleep) share one
 * anatomy shape (header -> chart -> big number -> trend); Favorite
 * moments/30D gets its own per-tag breakdown + deterministic insight line.
 * Nothing here is red, ever, including a declining trend (SS2's central
 * ruling) — up renders `hue.primary`, down renders the SAME `text.secondary`
 * tone as up, never a warm/red hue.
 */
export function TrailingAnalyticsCards({ analytics }: { readonly analytics: TrailingAnalyticsView }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 'var(--space-5)' }}>
      <MoodAnalyticsCard trend={analytics.moodTrend14d} />
      <EnergyAnalyticsCard trend={analytics.energyTrend14d} sleepTrend={analytics.sleepTrend14d} />
      <FavoriteMomentsCard breakdown={analytics.favoriteMoments30d} />
    </div>
  );
}

function AnalyticsCardShell({
  label,
  title,
  children,
}: {
  readonly label: string;
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <TodayCard label={label}>
      <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
        {title}
      </h2>
      <div style={{ marginTop: 'var(--space-4)' }}>{children}</div>
    </TodayCard>
  );
}

function TrendPill({ trend }: { readonly trend: TrendWindowView }) {
  if (trend.daysWithData < trend.windowDays || trend.trendPercent === null) {
    return <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>—</span>;
  }
  const up = trend.trendPercent >= 0;
  return (
    <span
      className="num"
      style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: up ? 'var(--hue-primary)' : 'var(--text-secondary)' }}
    >
      {up ? '↑' : '↓'} {Math.abs(trend.trendPercent)}%
    </span>
  );
}

function NoHistoryCaption() {
  return (
    <p style={{ margin: 0, fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', textAlign: 'center' }}>
      Give it a few days — trends show up once there&apos;s history to compare.
    </p>
  );
}

function MoodAnalyticsCard({ trend }: { readonly trend: TrendWindowView }) {
  if (trend.daysWithData === 0) {
    return (
      <AnalyticsCardShell label="Mood, last 14 days" title="Mood · Last 14 days">
        <NoHistoryCaption />
      </AnalyticsCardShell>
    );
  }

  return (
    <AnalyticsCardShell label="Mood, last 14 days" title="Mood · Last 14 days">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <span className="num" style={{ fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)' }}>
          {trend.average ?? '—'}
        </span>
        <TrendPill trend={trend} />
      </div>
      <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
        Based on {trend.daysWithData} of {trend.windowDays} days
      </p>
    </AnalyticsCardShell>
  );
}

function EnergyAnalyticsCard({ trend, sleepTrend }: { readonly trend: TrendWindowView; readonly sleepTrend: TrendWindowView }) {
  if (trend.daysWithData === 0) {
    return (
      <AnalyticsCardShell label="Energy, last 14 days" title="Energy · Last 14 days">
        <NoHistoryCaption />
      </AnalyticsCardShell>
    );
  }

  return (
    <AnalyticsCardShell label="Energy, last 14 days" title="Energy · Last 14 days">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <span className="num" style={{ fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)' }}>
          {trend.average ?? '—'}
        </span>
        <TrendPill trend={trend} />
      </div>
      {sleepTrend.average !== null && (
        <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
          {sleepTrend.average} hrs avg sleep
        </p>
      )}
    </AnalyticsCardShell>
  );
}

function FavoriteMomentsCard({ breakdown }: { readonly breakdown: TrailingAnalyticsView['favoriteMoments30d'] }) {
  if (breakdown.totalSelections === 0) {
    return (
      <AnalyticsCardShell label="Favorite moments, last 30 days" title="Favorite moments · 30D">
        <NoHistoryCaption />
      </AnalyticsCardShell>
    );
  }

  const bars = breakdown.byTag.filter((row) => row.sharePercent > 0).sort((a, b) => b.sharePercent - a.sharePercent);

  return (
    <AnalyticsCardShell label="Favorite moments, last 30 days" title="Favorite moments · 30D">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {bars.map((row) => (
          <div key={row.tag}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
              <span>{lifeAreaLabel(row.tag)}</span>
              <span className="num">{row.sharePercent}%</span>
            </div>
            <div style={{ marginTop: '2px', height: '6px', borderRadius: 'var(--radius-pill)', background: 'var(--border-subtle)', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${row.sharePercent}%`,
                  background: `var(${lifeAreaTokenPrefix(row.tag)}-vivid)`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <p
        style={{
          marginTop: 'var(--space-3)',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--surface-hover)',
          fontSize: 'var(--type-caption-size)',
          color: 'var(--text-secondary)',
        }}
      >
        {breakdown.insight ?? 'A pretty even mix this month.'}
      </p>
    </AnalyticsCardShell>
  );
}
